import * as ko from 'knockout'
import {ChecklistItemStateDto, ChecklistItemTagDto, ParticipantDto, TripDto} from '../../api/generated';
import globalState from "../../global-state";
import {myTripChecklistApi} from "../../api/api-wrapper";
import {postbox} from "../../components/util/postbox";
import {ChecklistItemEditable} from "./checklist";
import {App} from "../../app";
import {autobind, computed, observable} from "knockout-decorators";
import {isSharedTrip, participantIconColor, participantInitials, participantName} from "../share/shareUtils";
import ViewModelFunction = KnockoutComponentTypes.ViewModelFunction;


interface ViewModelParams {
    item: ChecklistItemEditable;
    items: KnockoutObservableArray<ChecklistItemEditable>;
    isSharedCategory: boolean;
}

class ViewModel {

    private checkedAnimationTimeout;

    // Passed as params and already observables.
    public item: ChecklistItemEditable;
    public items: KnockoutObservableArray<ChecklistItemEditable>;
    public checkedAnimationCompleted: KnockoutComputed<boolean>;
    public isSharedCategory: boolean;

    // Flag to avoid double clicks when the item is deleted
    @observable({deep: false, expose: false})
    public deleted: boolean;

    @observable({deep: false, expose: true})
    public renameState: boolean;

    @observable({deep: false, expose: true})
    public itemName: string;

    constructor(private params: ViewModelParams) {
        this.item = params.item;
        this.items = params.items;
        this.isSharedCategory = params.isSharedCategory;
        this.renameState = false;
        this.itemName = "";
        this.checkedAnimationCompleted = ko.pureComputed(() =>
            this.item.dto.checked == true && this.item.checkedAnimation == false
        );
        this.deleted = false;

        this.delete = this.delete.bind(this);
        this.toggleChecked = this.toggleChecked.bind(this);
        this.toggleRenameState = this.toggleRenameState.bind(this);
        this.rename = this.rename.bind(this);
    }

    /**
     * The current trip.
     */
    @computed({pure: true})
    public get trip(): TripDto {
        return globalState.currentTrip;
    }

    /**
     * The trip's participants.
     */
    @computed({pure: true})
    public get participants(): ParticipantDto[] {
        return globalState.currentParticipants;
    }

    /**
     * Delete a checklist item.
     */
    public delete() {
        globalState.loading(true);

        // avoid double clicks
        if (!this.deleted) {
            this.deleted = true;
            return myTripChecklistApi.deleteTripChecklistItem(String(this.trip.id), this.item.dto.id)
                .then(() => {
                    this.items().splice(this.items().indexOf(this.item), 1);
                    globalState.loading(false);
                })
                .catch(() => {
                    this.deleted = false;
                    postbox.addError('checklist.errors.failedToDeleteItem');
                    globalState.loading(false);
                });
        }
    }

    /**
     * Toggle the rename state.
     */
    public toggleRenameState() {
        this.renameState = !this.renameState;
        if (this.renameState) {
            this.itemName = this.item.dto.name;
        } else {
            this.itemName = "";
        }
    }

    /**
     * Rename a checklist item.
     */
    public rename() {
        // Validation
        if (this.itemName.trim().length < 1) {
            postbox.addInfo('checklist.errors.emptyItemName');
            return;
        }
        // Name has not changed
        if (this.item.dto.name == this.itemName) {
            this.toggleRenameState();
            return;
        }

        globalState.loading(true);

        const oldName = this.item.dto.name;
        this.item.dto.name = this.itemName;
        return myTripChecklistApi.updateTripChecklistItem(String(this.trip.id), this.item.dto.id, this.item.dto)
            .then(() => {
                this.toggleRenameState();
                globalState.loading(false);
            })
            .catch(() => {
                this.item.dto.name = oldName;
                postbox.addError('checklist.errors.failedToRenameItem');
                globalState.loading(false);
            });
    }

    /**
     * Toggle the checked property.
     */
    public toggleChecked() {
        globalState.loading(true);
        this.item.checkedAnimation = (this.item.dto.checked == false);

        // Animation is applied via css transitions and covers the first line of the text only.
        // When the animation is completed we have to set a css class to apply the strike through styles to multiple lines
        if (this.item.checkedAnimation == true) {
            clearTimeout(this.checkedAnimationTimeout);
            let self = this;
            this.checkedAnimationTimeout = setTimeout(function() {
                self.item.checkedAnimation = false;
                self.item.recentlyAdded = false;
            }, 500);
        }

        const itemState: ChecklistItemStateDto = {
            checked: !this.item.dto.checked
        };

        // update the flag immediately to start the animation and reset the flag if action failed.
        this.item.dto.checked = !this.item.dto.checked;
        return myTripChecklistApi.updateTripChecklistItemState(String(this.trip.id), this.item.dto.id, itemState)
            .then(() => {
                this.item.checkedAnimation = false;
                globalState.loading(false);
            })
            .catch(() => {
                this.item.dto.checked = !this.item.dto.checked;
                this.item.checkedAnimation = false;
                postbox.addError('checklist.errors.failedToCheckItem');
                globalState.loading(false);
            });
    }

    @autobind
    public assignHandler(participant: ParticipantDto) {
        return myTripChecklistApi.assignItemHandler(String(this.trip.id), this.item.dto.id, participant)
            .then(response => {
                this.item.dto.handler = participant;
                return response;
            })
            .catch(reason => {
                postbox.addError('share.edit.errors.assignItemHandler');
            })
    }

    @autobind
    public unassignHandler() {
        return myTripChecklistApi.unassignItemHandler(String(this.trip.id), this.item.dto.id)
            .then(response => {
                this.item.dto.handler = null;
                return response;
            })
            .catch(reason => {
                postbox.addError('share.edit.errors.unassignItemHandler');
            })
    }

    @computed
    public get hasHandler() {
        return typeof this.item.dto.handler !== 'undefined' && this.item.dto.handler != null;
    }

    @computed
    public get handlerInitials(): string {
        return this.hasHandler ? participantInitials(this.item.dto.handler) : '';
    }

    @autobind
    public isHandler(participant: ParticipantDto): boolean {
        return (!participant && !this.item.dto.handler)
            || (participant && this.item.dto.handler && this.item.dto.handler.id === participant.id);
    }

    public participantName(participant: ParticipantDto): string {
        return participantName(participant);
    }

    public participantInitials(participant: ParticipantDto): string {
        return participantInitials(participant);
    }

    public participantIconColor(idx: number) {
        return participantIconColor(idx);
    }

    @computed
    public get handlerIconColor() {
        // avoid js error when a handler is removed
        if (this.item.dto.handler) {
            const handlerId = this.item.dto.handler.id;
            const idx = this.participants.findIndex(participant => participant.id == handlerId);

            return participantIconColor(idx);
        }

        return 'auto';
    }

    /**
     * Check if the trip is shared.
     */
    @computed
    public get isSharedTrip() {
        return isSharedTrip(this.trip);
    }
}


const component: KnockoutComponentTypes.Config<ViewModelFunction> = {
    viewModel: (params: ViewModelParams) => new ViewModel(params),
    template: <string>require('./checklist-item.html')
};

export default component;

if (!ko.components.isRegistered('checklist-item')) {
    ko.components.register('checklist-item', component)
}
